type IdleCallback = (deadline: {
  didTimeout: boolean
  timeRemaining: () => number
}) => void

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: IdleCallback,
    options?: { timeout?: number },
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

export function runWhenIdle(callback: () => void, timeout = 3000) {
  if (import.meta.server) {
    return () => {}
  }

  let cancelled = false
  const run = () => {
    if (!cancelled) {
      callback()
    }
  }

  const idleWindow = window as IdleWindow
  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(run, { timeout })
    return () => {
      cancelled = true
      idleWindow.cancelIdleCallback?.(handle)
    }
  }

  // `timeout` is a deadline for `requestIdleCallback`, not a delay. Reusing it
  // as one here would turn "run at the next idle moment, at the latest in 3s"
  // into a flat 3s wait on every browser without the API (older iOS), which for
  // tracking callers is 3s of dropped events (ECOM-2851). Without an idle
  // signal, the next macrotask is the closest honest equivalent.
  const handle = window.setTimeout(run, 0)
  return () => {
    cancelled = true
    window.clearTimeout(handle)
  }
}

export function runAfterLoadWhenIdle(callback: () => void, timeout = 3000) {
  if (import.meta.server) {
    return () => {}
  }

  let cancelIdle = () => {}
  let cancelled = false

  const run = () => {
    if (!cancelled) {
      cancelIdle = runWhenIdle(callback, timeout)
    }
  }

  if (document.readyState === 'complete') {
    run()
    return () => {
      cancelled = true
      cancelIdle()
    }
  }

  window.addEventListener('load', run, { once: true })
  return () => {
    cancelled = true
    window.removeEventListener('load', run)
    cancelIdle()
  }
}
